const _ = require('lodash');
const moment = require('moment');
const jwt = require('jsonwebtoken');
const { DateTime } = require('luxon');

const requestTypeStrings = ['credit', 'non-credit', 'sandbox', 'external'];

function courseLabel({
    // eslint-disable-next-line camelcase
    subject, catalog_number, ssr_component, class_section, class_number, term,
}, hTermCodes) {
    // eslint-disable-next-line camelcase, max-len
    return `${subject} ${catalog_number} - ${ssr_component} ${class_section} - ${hTermCodes[term] ? hTermCodes[term].fullname.replace('Term ', '') : ''} - Class Number ${class_number}`;
}

function hashedTermCodes(termCodes) {
    const hterms = {};
    _.each(termCodes, (term) => {
        hterms[term.code] = term;
    });
    return hterms;
}

// Alberta observes ABT (UTC-6) year round; a fixed offset avoids depending on the browser's tzdata.
const ALBERTA_ZONE = 'UTC-6';

// PS term timestamps represent Alberta calendar dates; returns local midnight of that date to match the date pickers.
function albertaCalendarDate(iso) {
    return DateTime.fromISO(iso, { zone: ALBERTA_ZONE })
        .startOf('day')
        .setZone('local', { keepLocalTime: true });
}

function termOrTomorrowStartDate(termcode, hTermCodes) {
    if (hTermCodes[termcode]) {
        const tomorrow = DateTime.now().plus({ days: 1 }).startOf('day');
        const startOfTerm = albertaCalendarDate(hTermCodes[termcode].startdate);
        return startOfTerm > tomorrow ? startOfTerm : tomorrow;
    }
    throw new Error(`Term code ${termcode} not found in term codes`);
}

function termEndDate(termcode, hTermCodes) {
    if (hTermCodes[termcode]) {
        // Add 3 weeks after term end date for exams.
        return albertaCalendarDate(hTermCodes[termcode].enddate).plus({ days: 21 });
    }
    throw new Error(`Term code ${termcode} not found in term codes`);
}

function defaultStartDate(course, hTermCodes) {
    let startdate;
    _.each(course.coursedata, (cdata) => {
        const thedate = moment(albertaCalendarDate(hTermCodes[cdata.term].startdate).toJSDate());
        if (typeof startdate === 'undefined' || startdate.isAfter(thedate)) {
            startdate = thedate;
        }
    });
    return startdate.toDate();
}
function defaultEndDate(course, hTermCodes) {
    let enddate;
    _.each(course.coursedata, (cdata) => {
        const thedate = moment(albertaCalendarDate(hTermCodes[cdata.term].enddate).toJSDate()).add(3, 'w');
        if (typeof enddate === 'undefined' || enddate.isBefore(thedate)) {
            enddate = thedate;
        }
    });
    return enddate.toDate();
}

function selectedDateAfterDefaultStartDate(startDate, defStartDate) {
    return moment(startDate).isAfter(moment(defStartDate), 'day');
}

function selectedDateBeforeDefaultEndDate(endDate, defEndDate) {
    return moment(endDate).isBefore(moment(defEndDate), 'day');
}

/**
 * Adds tabindex to the button and also the handler for the click event
 *
 * @param {*} previous button
 */
function setTabindex(btnPrevious) {
    if (btnPrevious !== null) {
        if (!btnPrevious.hasAttribute('tabindex')) {
            btnPrevious.setAttribute('tabindex', '0');
            btnPrevious.addEventListener('keyup', (e) => {
                if (e.key === 'Enter') {
                    btnPrevious.click();
                }
            });
        }
    }
}

/**
 * Transforms the course queue into a compatible format for sending to the back end.
 *
 * Performs effectively 3 tasks:
 *  1. Translates frontend varable names such as 'term' to 'strm' and 'class_number' to 'class_nbr'.
 *  2. Adds optional settings and values based on the request type.
 *  3. Adds default start and end dates based on the term codes
 * @param {*} courseQueue Array of course configurations
 * @param {*} termCodes List of term codes
 * @param {int} requestType integer representing the type of request. 0 = 'credit', 1 = 'non-credit', 2 = sandbox, 3 = external
 */
function transformCourseQueueToRequest(courseQueue, termCodes, requestType) {
    const request = [];
    _.each(courseQueue, (course) => {
        const newC = {};
        newC.cohorts = [];
        _.each(course.coursedata, (cohort) => {
            newC.cohorts.push({
                strm: parseInt(cohort.term, 10),
                class_nbr: parseInt(cohort.class_number, 10),
                subject: cohort.subject,
                catalog_nbr: cohort.catalog_number,
                acad_group: cohort.acad_group,
                acad_org: cohort.acad_org,
                label: cohort.label,
            });
        });
        if (requestType === 0) {
            newC.coursevisibledate = moment(course.opendate).format('LL');
            newC.coursehidedate = moment(course.closedate).format('LL');
            newC.startdate = defaultStartDate(course, hashedTermCodes(termCodes));
            newC.enddate = defaultEndDate(course, hashedTermCodes(termCodes));
        }
        if (requestType === 1) {
            newC.phone = course.phone;
            newC.department = course.department;
        }
        newC.requesttype = requestTypeStrings[requestType];
        newC.fullname = course.name;
        newC.coursecontent = course.content === 'blank' || course.content === '' ? null : course.content;
        // Handle missing sources
        if (!course.contentsource) {
            newC.contentsource = 'eclass';
        } else {
            newC.contentsource = course.contentsource;
        }
        newC.requester = course.creator;
        newC.exclusions = {};
        newC.exclusions.excludequestionbank = course.wantExclusions;

        newC.enrollments = [];
        _.each(course.roleInstructor, (user) => {
            newC.enrollments.push({ username: user.ccid, role: 'instructor' });
        });
        _.each(course.roleNonEditTA, (user) => {
            newC.enrollments.push({ username: user.ccid, role: 'noneditta' });
        });
        _.each(course.roleTA, (user) => {
            newC.enrollments.push({ username: user.ccid, role: 'ta' });
        });
        _.each(course.roleDesigner, (user) => {
            newC.enrollments.push({ username: user.ccid, role: 'designer' });
        });
        // Append other non credit student details if specified to the special requests field.
        // eslint-disable-next-line prefer-template, max-len
        const nonCreditstudentInstructions = requestType === 1 && course.roleStudents !== '' && course.roleStudents !== 'manually' && course.roleStudents !== 'cohorts' ? ' | Other non-credit student details: ' + course.roleStudents : '';
        newC.specialrequests = course.specialInstructions ? course.specialInstructions + nonCreditstudentInstructions : nonCreditstudentInstructions;
        newC.specialrequests = newC.specialrequests === '' ? null : newC.specialrequests;

        request.push(newC);
    });
    return request;
}

function decodeCreator(idToken) {
    const decoded = jwt.decode(idToken);
    const keys = [
        'custom:ccid',
        'custom:displayName',
        'family_name',
        'custom:employeeNumber',
    ];
    if (!keys.every((k) => !!decoded[k])) {
        const missing = keys.filter((cur) => !decoded[cur]);
        throw Error(`Required key not found in id_token ${missing.join(',')}`);
    }
    return {
        ccid: decoded['custom:ccid'],
        empid: decoded['custom:employeeNumber'],
        name_ccid: `${decoded['custom:displayName']} ${decoded.family_name} (${decoded['custom:ccid']})`,
    };
}

const basePath = '/';

export {
    courseLabel,
    hashedTermCodes,
    defaultStartDate,
    defaultEndDate,
    selectedDateAfterDefaultStartDate,
    selectedDateBeforeDefaultEndDate,
    setTabindex,
    transformCourseQueueToRequest,
    basePath,
    decodeCreator,
    termOrTomorrowStartDate,
    termEndDate,
};
